import React, { useState, useRef, useMemo, useCallback } from 'react';
import ReactECharts from 'echarts-for-react';
import { Box, Typography, IconButton, Menu, MenuItem, CircularProgress, Tooltip } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import { EChartsOption, DataZoomComponentOption } from 'echarts';
import { exportChartToPng } from '../../utils/chartUtils';
import throttle from 'lodash/throttle';
import debounce from 'lodash/debounce';

// Type definitions
export interface LineChartDataItem {
  name: string;
  [key: string]: string | number;
}

export interface LineSeries {
  dataKey: string;
  name?: string;
  color?: string;
  dashArray?: string;
  type?: 'line' | 'bar' | 'scatter';
  strokeWidth?: number;
  showSymbol?: boolean;
  smooth?: boolean;
  area?: boolean;
  areaOpacity?: number;
}

export interface ELineChartProps {
  /** Chart data */
  data: LineChartDataItem[];
  /** Data series to display */
  series: LineSeries[];
  /** Chart title */
  title?: string;
  /** Chart height */
  height?: number;
  /** X-axis label */
  xAxisLabel?: string;
  /** Y-axis label */
  yAxisLabel?: string;
  /** Show chart grid */
  grid?: boolean;
  /** Format tooltips */
  tooltipFormatter?: (value: number | string) => string;
  /** Reference line value (e.g., for thresholds) */
  referenceLine?: { y?: number; x?: string | number; label?: string; color?: string };
  /** Enable zooming */
  zoom?: boolean;
  /** Enable data brushing */
  showBrush?: boolean;
  /** Data is refreshing status */
  isRefreshing?: boolean;
  /** Last updated timestamp */
  lastUpdated?: Date;
  /** Function to refresh data */
  onRefresh?: () => void;
}

/**
 * Line chart component implemented with ECharts
 */
const ELineChart: React.FC<ELineChartProps> = ({
  data,
  series,
  title,
  height = 400,
  xAxisLabel,
  yAxisLabel,
  grid = true,
  tooltipFormatter,
  referenceLine,
  zoom = false,
  showBrush = false,
  isRefreshing = false,
  onRefresh,
}) => {
  const chartRef = useRef<ReactECharts>(null);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const exportMenuOpen = Boolean(exportMenuAnchor);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(new Date());

  // Convert data and series to ECharts format with proper dependency tracking
  const seriesData = useMemo(() => {
    return series.map(serie => {
      const lineStyle: {
        width?: number;
        type?: 'solid' | 'dashed' | 'dotted';
        color?: string;
      } = {
        width: serie.strokeWidth || 2,
        color: serie.color
      };

      if (serie.dashArray) {
        lineStyle.type = 'dashed';
      }

      const areaStyle = serie.area 
        ? {
            opacity: serie.areaOpacity || 0.2,
            color: serie.color
          }
        : undefined;

      return {
        name: serie.name || serie.dataKey,
        type: serie.type || 'line',
        data: data.map(item => [item.name, item[serie.dataKey]]),
        showSymbol: serie.showSymbol !== false,
        symbolSize: 6,
        smooth: serie.smooth || false,
        lineStyle,
        areaStyle,
        itemStyle: {
          color: serie.color
        }
      };
    });
  }, [data, series]);

  // Create debounced refresh handler for performance
  const handleRefresh = useCallback(debounce(() => {
    if (onRefresh) {
      onRefresh();
      setLastUpdated(new Date());
    }
  }, 300), [onRefresh]);

  // Create ECharts options with proper dependencies
  const options: EChartsOption = useMemo(() => ({
    title: title ? {
      text: title,
      left: 'center'
    } : undefined,
    tooltip: {
      trigger: 'axis',
      formatter: tooltipFormatter 
        ? (params: any) => {
            const p = Array.isArray(params) ? params[0] : params;
            return tooltipFormatter(p.value[1] as number | string);
          }
        : undefined
    },
    legend: {
      data: series.map(s => s.name || s.dataKey),
      bottom: 0
    },
    grid: {
      left: yAxisLabel ? '10%' : '3%',
      right: '4%',
      bottom: '15%',
      top: title ? '15%' : '10%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      name: xAxisLabel,
      nameLocation: 'middle',
      nameGap: 30,
      axisLine: {
        show: true
      },
      axisLabel: {
        rotate: data.length > 10 ? 45 : 0
      }
    },
    yAxis: {
      type: 'value',
      name: yAxisLabel,
      nameLocation: 'middle',
      nameGap: 50,
      axisLine: {
        show: true
      }
    },
    series: seriesData,
    // Add zoom functionality if enabled    
    dataZoom: zoom || showBrush ? [
      {
        type: 'inside',
        start: 0,
        end: 100
      } as DataZoomComponentOption,
      ...(showBrush ? [{
        type: 'slider',
        start: 0,
        end: 100,
        height: 30,
        bottom: 5,
        borderColor: 'rgba(0,0,0,0.2)',
        fillerColor: 'rgba(0,0,0,0.1)'
      } as DataZoomComponentOption] : [])
    ] as DataZoomComponentOption[] : undefined,
    // Add reference line if provided
    markLine: referenceLine?.y ? {
      data: [
        {
          name: referenceLine.label || 'Reference',
          yAxis: referenceLine.y,
          lineStyle: {
            color: referenceLine.color || 'red',
            type: 'dashed'
          },
          label: {
            show: true,
            formatter: referenceLine.label || '{c}'
          }
        }
      ]
    } : undefined
  }), [title, tooltipFormatter, seriesData, xAxisLabel, yAxisLabel, grid, zoom, showBrush, referenceLine]);

  const handleRefreshClick = () => {
    handleRefresh();
  };

  const handleExportMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };
  
  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };
  
  const handleExportPng = () => {
    handleExportMenuClose();
    if (chartRef.current) {
      const echartInstance = chartRef.current.getEchartsInstance();
      const dataURL = echartInstance.getDataURL();
      const link = document.createElement('a');
      link.download = `${title || 'line-chart'}.png`;
      link.href = dataURL;
      link.click();
    }
  };

  const formattedLastUpdated = lastUpdated 
    ? `Updated: ${lastUpdated.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit', second:'2-digit'})}`
    : '';

  return (
    <Box sx={{ width: '100%', mb: 3, position: 'relative' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        {title && <Typography variant="h6" sx={{ fontWeight: 'medium' }}>{title}</Typography>}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {lastUpdated && (
            <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', mr: 2 }}>
              {formattedLastUpdated}
            </Box>
          )}
          
          {onRefresh && (
            <Tooltip title="Refresh data">
              <IconButton size="small" onClick={handleRefreshClick} disabled={isRefreshing}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          
          <Tooltip title="Export options">
            <IconButton size="small" onClick={handleExportMenuOpen}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={exportMenuAnchor}
            open={exportMenuOpen}
            onClose={handleExportMenuClose}
          >
            <MenuItem onClick={handleExportPng}>Export as PNG</MenuItem>
          </Menu>
        </Box>
      </Box>
        <Box
        sx={{
          width: '100%',
          borderRadius: 1,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          position: 'relative'
        }}
      >
        {isRefreshing && (
          <Box 
            sx={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
              right: 0, 
              bottom: 0, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              background: 'rgba(255, 255, 255, 0.7)', 
              borderRadius: 1, 
              zIndex: 5 
            }}
          >
            <CircularProgress size={30} />
          </Box>
        )}
        
        <ReactECharts
          ref={chartRef}
          option={options}
          style={{ height: height }}
          opts={{ renderer: 'canvas' }}
          notMerge={true}
        />
      </Box>
    </Box>
  );
};

// Export with React.memo for performance optimization
export default React.memo(ELineChart);
